import AdminOrderSummary from './AdminOrderSummary';
import { orderItemKey, orderItemReference } from '../../../../utils/orderItemKeys';
import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Link } from 'react-router-dom';
import { searchTransferOrders, transferOrderItems } from '../../../../api/orderItemsApi';
import { getApiErrorText } from '../../../../utils/apiResponse';
import { orderReadyForShipment } from '../../../../utils/stockQuantities';
import {
  getClassNameForProductStatus,
} from '../../../../utils/adminOrderService';

const InformationNotice = ({ title, children, className = '' }) => (
  <div className={`alert alert-info d-flex small mb-0 ${className}`} role='note'>
    <i className='bi bi-info-circle mr-2 flex-shrink-0' aria-hidden='true' />
    <div>
      <strong className='d-block mb-1'>{title}</strong>
      {children}
    </div>
  </div>
);


export default function TransferOrderItemsModal({
  sourceOrderId,
  items,
  mode,
  orderStatuses = [],
  onClose,
  onTransferred,
}) {
  const split = mode === 'split';
  const [search, setSearch] = useState('');
  const [orders, setOrders] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [targetOrderId, setTargetOrderId] = useState(null);
  const [splitOrderStatus, setSplitOrderStatus] = useState('NEW');
  const itemsReadyForShipment = orderReadyForShipment({ orderItems: items });
  const splitStatuses = [{ databaseValue: 'NEW', adminValue: 'Нове' },
    ...orderStatuses.filter(status => status.databaseValue !== 'NEW')]
    .filter(status => !['CANCELLED', 'RETURNED'].includes(status.databaseValue))
    .filter(status => itemsReadyForShipment ||
      !['WAITING_FOR_PACKAGING', 'WAITING_FOR_SENDING', 'SENT'].includes(status.databaseValue));
  const effectiveSplitStatus = splitStatuses.some(status => status.databaseValue === splitOrderStatus)
    ? splitOrderStatus : 'NEW';
  const savingLock = useRef(false);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  useEffect(() => {
    if (split || selectedOrder || targetOrderId) return;
    let current = true;
    const controller = new AbortController();
    setSearching(true);
    setOrders([]);
    setSearchError('');
    const timer = setTimeout(() => {
      searchTransferOrders(search, sourceOrderId, controller.signal)
        .then(result => {
          if (current) setOrders(result);
        })
        .catch(error => {
          if (current) setSearchError(getApiErrorText(error, 'Не вдалося знайти замовлення'));
        })
        .finally(() => {
          if (current) setSearching(false);
        });
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [search, sourceOrderId, split, selectedOrder, targetOrderId]);

  const submit = async event => {
    event.preventDefault();
    if (savingLock.current || targetOrderId || items.length === 0 || (!split && !selectedOrder))
      return;
    savingLock.current = true;
    setSaving(true);
    setSaveError('');
    try {
      const selectedItems = items.map(orderItemReference);
      const response = await (split
        ? transferOrderItems(sourceOrderId, selectedItems, null, effectiveSplitStatus)
        : transferOrderItems(sourceOrderId, selectedItems, selectedOrder.orderId));
      if (!mounted.current) return;
      const destination = response.data.data.targetOrderId;
      setTargetOrderId(destination);
      onTransferred(destination, Boolean(response.data.data.sourceOrderDeleted));
    } catch (error) {
      if (mounted.current) setSaveError(getApiErrorText(error, 'Не вдалося перенести товари'));
    } finally {
      savingLock.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <Modal
      show
      onHide={() => !savingLock.current && onClose()}
      backdrop='static'
      keyboard={!saving}
      centered
      aria-labelledby='transfer-order-title'
    >
      <form onSubmit={submit}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title as='h2' id='transfer-order-title' className='h5 font-weight-bold mb-0'>
            {split ? 'Виділити в окреме замовлення' : 'Перенести в інше замовлення'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='small text-muted'>До відправлення промокоди перераховуються для кожного замовлення окремо. При перенесенні всіх товарів у замовлення без промокоду код переноситься разом із ними. Після відправлення товари зберігають уже розподілену знижку; інший промокод із нею не поєднується.</p>
          {targetOrderId ? (
            <div className='alert alert-success mb-0' role='status'>
              {split ? 'Створено замовлення' : 'Товари перенесено в замовлення'}{' '}
              <Link to={`/admin/order/${targetOrderId}`} onClick={onClose}>
                #{targetOrderId}
              </Link>
              .
            </div>
          ) : (
            <>
              <section className='mb-4' aria-labelledby='transfer-products-heading'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h3 id='transfer-products-heading' className='h6 font-weight-bold mb-0'>
                    Обрані товари <span className='badge badge-light ml-1'>{items.length}</span>
                  </h3>
                  <span className='small text-muted'>Із замовлення #{sourceOrderId}</span>
                </div>
                <ul
                  className='list-unstyled small border rounded bg-light px-3 py-2 mb-0'
                  style={{ maxHeight: '180px', overflowY: 'auto' }}
                >
                  {items.map(item => (
                    <li key={orderItemKey(item)} className='d-flex justify-content-between py-1'>
                      <div className='mr-3'>
                        <div>{item.name}</div>
                        {item.productStatus && (
                          <span className={`badge badge-light ${getClassNameForProductStatus(item.productStatus.databaseValue)}`}>
                            {item.productStatus.shortValue || item.productStatus.clientValue}
                          </span>
                        )}
                      </div>
                      <span className='font-weight-bold text-nowrap'>{item.quantity} шт.</span>
                    </li>
                  ))}
                </ul>
              </section>
              <section className='mb-3' aria-labelledby='transfer-destination-heading'>
                <h3 id='transfer-destination-heading' className='h6 font-weight-bold mb-2'>
                  {split ? 'Нове замовлення' : 'Замовлення призначення'}
                </h3>
                {split ? (
                  <>
                  <div className='form-group'>
                    <label htmlFor='split-order-status'>Статус нового замовлення</label>
                    <select id='split-order-status' className='form-control' value={effectiveSplitStatus}
                      disabled={saving} onChange={event => setSplitOrderStatus(event.target.value)}>
                      {splitStatuses.map(status => <option key={status.databaseValue} value={status.databaseValue}>
                        {status.adminValue}
                      </option>)}
                    </select>
                  </div>
                  <InformationNotice title='Що буде скопійовано'>
                    Дані клієнта, доставка, спосіб оплати та коментарі початкового замовлення.
                    ТТН і номер Prom не копіюються.
                  </InformationNotice>
                  </>
                ) : selectedOrder ? (
                  <div className='border rounded p-3 mb-3'>
                    <AdminOrderSummary order={selectedOrder} />
                    <button
                      type='button'
                      className='btn btn-sm btn-outline-secondary mt-2'
                      disabled={saving}
                      onClick={() => setSelectedOrder(null)}
                    >
                      Змінити замовлення
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      id='transfer-order-search'
                      aria-label='Пошук замовлення'
                      className='form-control mb-2'
                      autoFocus
                      placeholder='Номер замовлення або прізвище'
                      value={search}
                      disabled={saving}
                      onChange={event => setSearch(event.target.value)}
                    />
                    {searching && (
                      <p className='small text-muted' role='status'>
                        Пошук замовлень…
                      </p>
                    )}
                    {searchError && (
                      <div className='alert alert-danger' role='alert'>
                        {searchError}
                      </div>
                    )}
                    {!searching && !searchError && orders.length === 0 && (
                      <p className='small text-muted'>Замовлень не знайдено.</p>
                    )}
                    <div
                      className='list-group mb-3'
                      style={{ maxHeight: '240px', overflowY: 'auto' }}
                    >
                      {orders.map(order => (
                        <button
                          key={order.orderId}
                          type='button'
                          className='list-group-item list-group-item-action'
                          onClick={() => {
                            setSelectedOrder(order);
                            setSaveError('');
                          }}
                          disabled={saving}
                        >
                          <AdminOrderSummary order={order} />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </section>
              <InformationNotice title='Перерахунок оплат'>
                Суми замовлень і статуси оплат буде перераховано. Зайву суму оплати буде відкріплено
                спочатку з часткових оплат, потім із повних. Вона стане доступною для повторного прикріплення.
                {' '}Якщо в початковому замовленні не залишиться товарів, його буде видалено.
              </InformationNotice>
              {saveError && (
                <div className='alert alert-danger mt-3 mb-0' role='alert'>
                  {saveError}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-secondary' onClick={onClose} disabled={saving}>
            Закрити
          </button>
          {!targetOrderId && (
            <button
              type='submit'
              className='btn btn-dark'
              disabled={saving || items.length === 0 || (!split && !selectedOrder)}
            >
              {saving ? 'Перенесення…' : split ? 'Створити замовлення' : 'Перенести товари'}
            </button>
          )}
        </Modal.Footer>
      </form>
    </Modal>
  );
}
